import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { api, formatApiErrorDetail } from "@/lib/api";

const todayISO = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
};

const DAYS_HR = ["nedjelja", "ponedjeljak", "utorak", "srijeda", "četvrtak", "petak", "subota"];
const MONTHS_HR = ["siječnja", "veljače", "ožujka", "travnja", "svibnja", "lipnja", "srpnja", "kolovoza", "rujna", "listopada", "studenoga", "prosinca"];

const prettyDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return `${DAYS_HR[dt.getDay()]}, ${d}. ${MONTHS_HR[m - 1]} ${y}.`;
};

const isSunday = (iso) => {
  if (!iso) return false;
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).getDay() === 0;
};

// Working hours per weekday (0=Sun … 6=Sat). Empty array = closed.
const WORKING_HOURS = {
  0: [],                                                            // Nedjelja — zatvoreno
  1: ["13:00", "13:30", "14:00", "14:30", "15:00", "15:30", "16:00", "16:30", "17:00", "17:30", "18:00", "18:30", "19:00", "19:30"], // Pon 13–20
  2: ["08:00", "08:30", "09:00", "09:30", "10:00", "10:30", "11:00", "11:30", "12:00", "12:30", "13:00", "13:30", "14:00", "14:30"], // Utorak 08–15
  3: ["13:00", "13:30", "14:00", "14:30", "15:00", "15:30", "16:00", "16:30", "17:00", "17:30", "18:00", "18:30", "19:00", "19:30"], // Sri 13–20
  4: ["13:00", "13:30", "14:00", "14:30", "15:00", "15:30", "16:00", "16:30", "17:00", "17:30", "18:00", "18:30", "19:00", "19:30"], // Čet 13–20
  5: ["08:00", "08:30", "09:00", "09:30", "10:00", "10:30", "11:00", "11:30", "12:00", "12:30", "13:00", "13:30", "14:00", "14:30"], // Pet 08–15
  6: ["08:00", "08:30", "09:00", "09:30", "10:00", "10:30", "11:00", "11:30", "12:00", "12:30", "13:00", "13:30"],                   // Sub 08–14
};

const dayOfWeek = (iso) => {
  if (!iso) return null;
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).getDay();
};

const isClosed = (iso) => {
  const dow = dayOfWeek(iso);
  return dow === null || WORKING_HOURS[dow].length === 0;
};

const slotsForDate = (iso) => {
  const dow = dayOfWeek(iso);
  if (dow === null) return [];
  return WORKING_HOURS[dow];
};

export const BookingDialog = ({ open, onOpenChange, preselectedService }) => {
  const [categories, setCategories] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(() => ({
    service_id: preselectedService || "",
    date: "",
    time: "",
    name: "",
    phone: "",
    email: "",
    note: "",
  }));

  useEffect(() => {
    if (!open) return;
    api.get("/services").then(({ data }) => setCategories(data)).catch(() => {});
  }, [open]);

  const handleOpenChange = (nextOpen) => {
    onOpenChange(nextOpen);
  };

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.service_id) { toast.error("Odaberite uslugu."); return; }
    if (!form.date) { toast.error("Odaberite datum."); return; }
    if (isClosed(form.date)) { toast.error("Salon je zatvoren tog dana — odaberite drugi datum."); return; }
    if (!form.time) { toast.error("Odaberite vrijeme."); return; }

    setSubmitting(true);
    try {
      await api.post("/reservations", {
        service_id: form.service_id,
        date: form.date,
        time: form.time,
        name: form.name,
        phone: form.phone,
        email: form.email,
        note: form.note,
      });
      toast.success("Vaša rezervacija je zaprimljena", {
        description: "Javit ćemo vam se uskoro radi potvrde termina.",
      });
      onOpenChange(false);
      setForm({ service_id: "", date: "", time: "", name: "", phone: "", email: "", note: "" });
    } catch (err) {
      toast.error("Greška pri rezervaciji", {
        description: formatApiErrorDetail(err.response?.data?.detail) || err.message,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="booking-dialog"
        className="bg-[#061A12] border border-[#C9A86A]/25 rounded-none max-w-2xl text-[#F5EFE3] p-0 max-h-[92vh] overflow-y-auto"
      >
        <div className="relative grain">
          <div className="relative z-10 p-8 md:p-10">
            <DialogHeader className="space-y-3">
              <span className="font-sans tracking-luxury text-[0.65rem] uppercase text-gold">Rezervacija termina</span>
              <DialogTitle className="font-serif font-light text-3xl md:text-4xl text-[#F5EFE3]">
                Priuštite svojoj kosi <span className="text-gold italic">luksuz</span>.
              </DialogTitle>
              <DialogDescription className="text-[#B0C4B1] font-sans text-sm">
                Ispunite obrazac i javit ćemo vam se uskoro radi potvrde termina.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={submit} className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-7">
              {/* Service */}
              <div className="md:col-span-2">
                <label className="font-sans tracking-wide-2 text-[0.65rem] uppercase text-gold/80 block mb-2">
                  Usluga *
                </label>
                <Select value={form.service_id} onValueChange={(v) => update("service_id", v)}>
                  <SelectTrigger
                    data-testid="booking-service-trigger"
                    className="bg-transparent border-0 border-b border-[#C9A86A]/30 rounded-none focus:ring-0 focus:border-gold text-[#F5EFE3] h-11 px-0"
                  >
                    <SelectValue placeholder="Odaberite uslugu" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#061A12] border-[#C9A86A]/30 text-[#F5EFE3] max-h-[60vh]">
                    {categories.map((cat) => (
                      <SelectGroup key={cat.id}>
                        <SelectLabel className="text-gold/70 font-sans tracking-luxury text-[0.6rem] uppercase pt-3">
                          {cat.name}
                        </SelectLabel>
                        {cat.items.map((s) => (
                          <SelectItem key={s.id} value={s.id} data-testid={`service-opt-${s.id}`} className="focus:bg-[#0F3D2E] focus:text-gold pl-6">
                            <span className="flex items-center justify-between gap-6 w-full">
                              <span>{s.name}{s.unit ? ` (${s.unit})` : ""}</span>
                              <span className="text-gold font-serif ml-3">{s.price} €</span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Date - native HTML5 picker */}
              <div>
                <label className="font-sans tracking-wide-2 text-[0.65rem] uppercase text-gold/80 block mb-2">
                  Datum *
                </label>
                <input
                  data-testid="booking-date-input"
                  className="input-luxury cursor-pointer date-input-luxury"
                  type="date"
                  value={form.date}
                  min={todayISO()}
                  onChange={(e) => { update("date", e.target.value); update("time", ""); }}
                  required
                />
                {form.date && (
                  <p className={`mt-2 font-serif text-sm ${isClosed(form.date) ? "text-red-300" : "text-[#B0C4B1]"}`}>
                    {prettyDate(form.date)} {isClosed(form.date) && "· salon je zatvoren"}
                  </p>
                )}
              </div>

              {/* Time */}
              <div>
                <label className="font-sans tracking-wide-2 text-[0.65rem] uppercase text-gold/80 block mb-2">
                  Vrijeme *
                </label>
                <Select value={form.time} onValueChange={(v) => update("time", v)} disabled={!form.date || isClosed(form.date)}>
                  <SelectTrigger
                    data-testid="booking-time-trigger"
                    className="bg-transparent border-0 border-b border-[#C9A86A]/30 rounded-none focus:ring-0 focus:border-gold text-[#F5EFE3] h-11 px-0 disabled:opacity-50"
                  >
                    <SelectValue placeholder={!form.date ? "Prvo odaberite datum" : isClosed(form.date) ? "Zatvoreno" : "Odaberite vrijeme"} />
                  </SelectTrigger>
                  <SelectContent className="bg-[#061A12] border-[#C9A86A]/30 text-[#F5EFE3] max-h-64">
                    {slotsForDate(form.date).map((t) => (
                      <SelectItem key={t} value={t} className="focus:bg-[#0F3D2E] focus:text-gold">
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Name */}
              <div>
                <label className="font-sans tracking-wide-2 text-[0.65rem] uppercase text-gold/80 block mb-2">
                  Ime i prezime *
                </label>
                <input
                  data-testid="booking-name-input"
                  className="input-luxury"
                  type="text"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  placeholder="Ana Horvat"
                  required
                />
              </div>

              {/* Phone */}
              <div>
                <label className="font-sans tracking-wide-2 text-[0.65rem] uppercase text-gold/80 block mb-2">
                  Telefon *
                </label>
                <input
                  data-testid="booking-phone-input"
                  className="input-luxury"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  placeholder="091 234 5678"
                  required
                />
              </div>

              {/* Email */}
              <div className="md:col-span-2">
                <label className="font-sans tracking-wide-2 text-[0.65rem] uppercase text-gold/80 block mb-2">
                  Email *
                </label>
                <input
                  data-testid="booking-email-input"
                  className="input-luxury"
                  type="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  placeholder="vas@email.com"
                  required
                />
              </div>

              {/* Note */}
              <div className="md:col-span-2">
                <label className="font-sans tracking-wide-2 text-[0.65rem] uppercase text-gold/80 block mb-2">
                  Napomena
                </label>
                <textarea
                  data-testid="booking-note-input"
                  className="input-luxury resize-none"
                  rows={3}
                  value={form.note}
                  onChange={(e) => update("note", e.target.value)}
                  placeholder="Dužina kose, željeni izgled, alergije…"
                />
              </div>

              <div className="md:col-span-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-4 mt-2">
                <button
                  type="submit"
                  data-testid="booking-submit-btn"
                  disabled={submitting}
                  className="btn-luxury btn-luxury-solid disabled:opacity-50 disabled:cursor-not-allowed flex-1 justify-center"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Šaljemo…
                    </>
                  ) : (
                    "Pošalji rezervaciju"
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => onOpenChange(false)}
                  data-testid="booking-cancel-btn"
                  className="font-sans tracking-luxury uppercase text-xs text-[#B0C4B1] hover:text-gold transition px-4"
                >
                  Odustani
                </button>
              </div>
            </form>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
